/**
 * Special-use attributes (RFC 6154, plus Nylas \Inbox and \Important) and mailbox flags
 * for folders that cannot hold mail. IMAP servers also send \HasNoChildren, \Marked, and
 * \Unmarked on every folder, so a leading backslash alone does not mean system folder.
 */
const NON_CATEGORY_ATTRIBUTES = new Set([
  '\\inbox',
  '\\sent',
  '\\drafts',
  '\\junk',
  '\\trash',
  '\\archive',
  '\\important',
  '\\flagged',
  '\\all',
  '\\noselect',
  '\\nonexistent',
  'starred',
  'unread',
]);

/** User folders and labels, shown as categories. System folders stay in the upper rail. */
export function isCategoryFolder(folder: {
  system_folder?: boolean | null;
  attributes?: string[] | null;
}): boolean {
  if (folder.system_folder) return false;
  return !(folder.attributes ?? []).some((attr) => NON_CATEGORY_ATTRIBUTES.has(attr.toLowerCase()));
}

/** Outlook PST imports arrive as one Gmail label whose name is the full folder path. */
export function folderRailName(name: string): { label: string; title: string } {
  const trimmed = name.trim();
  const parts = trimmed.split('/').map((part) => part.trim()).filter(Boolean);
  const archiveRoot = parts[0]?.toLowerCase().endsWith('.pst') ? parts[0] : '';
  const body = archiveRoot ? parts.slice(1) : parts;
  if (body.length === 0) {
    const email = archiveRoot.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0];
    const label = email || trimmed || 'Ordner';
    return { label, title: trimmed || label };
  }
  const label = body[body.length - 1] || trimmed;
  const title = body.join(' / ');
  return { label, title };
}

/** Keep a PST path, only replace the leaf the user edited. */
export function replaceFolderLeafName(storedName: string, nextLeaf: string): string {
  const leaf = nextLeaf.trim();
  if (!leaf) return storedName.trim();
  const trimmed = storedName.trim();
  const parts = trimmed.split('/').map((part) => part.trim()).filter(Boolean);
  if (parts.length <= 1) return leaf;
  parts[parts.length - 1] = leaf;
  return parts.join('/');
}

export function folderLabelsForRail(rows: Array<{ id: string; name: string }>): Map<string, { label: string; title: string }> {
  const named = rows.map((row) => ({ id: row.id, rail: folderRailName(row.name) }));
  const counts = new Map<string, number>();
  for (const row of named) counts.set(row.rail.label, (counts.get(row.rail.label) ?? 0) + 1);
  const result = new Map<string, { label: string; title: string }>();
  for (const row of named) {
    const parts = row.rail.title.split(' / ');
    const parent = parts.length > 1 ? parts[parts.length - 2] : '';
    const shared = (counts.get(row.rail.label) ?? 0) > 1;
    const label = shared && parent ? `${parent} / ${row.rail.label}` : row.rail.label;
    result.set(row.id, { label, title: row.rail.title });
  }
  return result;
}
